/**
 * Export service contract (#3151): the ONE request/response vocabulary every export
 * (CSV, Excel, PDF, JSON) is created and read with. The backend route, the frontend
 * client and the tests all parse these objects, so a field cannot exist on one side only.
 *
 * Two type names differ from the plan because `admin/gdpr.types.ts` already exports
 * `ExportFormat` and `ExportStatus` from this package: here they are `ExportFileFormat`
 * and `ExportJobStatus`.
 */

import { z } from 'zod'

/** Dataset keys. Each dataset adds its key in the piece that implements it. */
export const EXPORT_DATASET_KEYS = ['matching.legs'] as const

export const exportDatasetKeySchema = z.enum(EXPORT_DATASET_KEYS)

export const exportFormatSchema = z.enum(['csv', 'xlsx', 'pdf', 'json'])

/** Maximum number of ids a selection scope may carry. */
export const EXPORT_SELECTION_MAX_IDS = 10_000

export const exportScopeSchema = z.discriminatedUnion('kind', [
  // The query is validated by the dataset's own schema, not here.
  z.object({ kind: z.literal('filter'), query: z.unknown() }).strict(),
  z
    .object({
      kind: z.literal('selection'),
      ids: z.array(z.string().uuid()).min(1).max(EXPORT_SELECTION_MAX_IDS),
    })
    .strict(),
])

export const createExportRequestSchema = z
  .object({
    dataset: exportDatasetKeySchema,
    format: exportFormatSchema,
    scope: exportScopeSchema,
    columns: z.array(z.string()).min(1).optional(),
  })
  .strict()

export const exportStatusSchema = z.enum([
  'queued',
  'running',
  'ready',
  'failed',
  'cancelled',
  'expired',
])

export const exportResourceSchema = z.object({
  id: z.string().uuid(),
  dataset: exportDatasetKeySchema,
  format: exportFormatSchema,
  status: exportStatusSchema,
  rowsTotal: z.number().int().nullable(),
  rowsWritten: z.number().int(),
  fileName: z.string().nullable(),
  bytes: z.number().int().nullable(),
  createdAt: z.string(),
  readyAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
  error: z.object({ code: z.string(), message: z.string() }).nullable(),
})

/**
 * Answer of `POST /exports/preview` (#3152): the count the dialog shows BEFORE anything is
 * created, with the limits and the conventions the file would be written with.
 */
export const exportPreviewResponseSchema = z.object({
  rowCount: z.number().int(),
  maxRows: z.number().int(),
  estimatedBytes: z.number().int(),
  formats: z.array(exportFormatSchema).min(1),
  columns: z.array(z.object({ key: z.string(), label: z.string(), defaultSelected: z.boolean() })),
  conventions: z.object({
    fieldSeparator: z.string(),
    decimalSeparator: z.string(),
    dateFormat: z.string(),
    timeZone: z.string(),
  }),
  retentionHours: z.number().int(),
})

/** Stable error codes of the export service; the client maps each to a localized message. */
export const EXPORT_ERROR_CODES = {
  TOO_LARGE: 'EXPORT_TOO_LARGE',
  CONCURRENCY_LIMIT: 'EXPORT_CONCURRENCY_LIMIT',
  XLSX_ROW_LIMIT: 'EXPORT_XLSX_ROW_LIMIT',
  NOT_FOUND: 'EXPORT_NOT_FOUND',
  EXPIRED: 'EXPORT_EXPIRED',
  COLUMN_UNKNOWN: 'EXPORT_COLUMN_UNKNOWN',
} as const

export type ExportErrorCode = (typeof EXPORT_ERROR_CODES)[keyof typeof EXPORT_ERROR_CODES]
export type ExportDatasetKey = z.infer<typeof exportDatasetKeySchema>
export type ExportFileFormat = z.infer<typeof exportFormatSchema>
export type ExportScope = z.infer<typeof exportScopeSchema>
export type CreateExportRequest = z.infer<typeof createExportRequestSchema>
export type ExportJobStatus = z.infer<typeof exportStatusSchema>
export type ExportResource = z.infer<typeof exportResourceSchema>
export type ExportPreviewResponse = z.infer<typeof exportPreviewResponseSchema>
